import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Ban, CheckCircle, Clock, XCircle, Loader2, ChevronDown, ChevronRight, Download } from "lucide-react";
import { clsx } from "clsx";
import { useAuthStore } from "@/store/auth";
import { bgTasksApi } from "../api/bgTasks";
import type { AsyncTaskRecord, TaskDevError, TaskStatus } from "../types";

export { Activity };

export const STATUS_OPTIONS: { value: TaskStatus | ""; label: string }[] = [
  { value: "", label: "Wszystkie statusy" },
  { value: "pending", label: "Oczekuje" },
  { value: "running", label: "W trakcie" },
  { value: "success", label: "Sukces" },
  { value: "failure", label: "Błąd" },
  { value: "cancelled", label: "Anulowano" },
];

export const inputCls =
  "w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function StatusBadge({ status }: { status: TaskStatus }) {
  const map: Record<TaskStatus, { label: string; cls: string; icon: React.ReactNode }> = {
    pending: {
      label: "Oczekuje",
      cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
      icon: <Clock size={12} />,
    },
    running: {
      label: "W trakcie",
      cls: "bg-blue-100 text-[var(--color-primary)] dark:bg-blue-900/40 dark:text-blue-300",
      icon: <Loader2 size={12} className="animate-spin" />,
    },
    success: {
      label: "Sukces",
      cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
      icon: <CheckCircle size={12} />,
    },
    failure: {
      label: "Błąd",
      cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
      icon: <XCircle size={12} />,
    },
    cancelled: {
      label: "Anulowano",
      cls: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
      icon: <Ban size={12} />,
    },
  };
  const { label, cls, icon } = map[status] ?? map.pending;
  return (
    <span className={clsx("inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded text-xs font-medium", cls)}>
      {icon}
      {label}
    </span>
  );
}

export function TaskRow({ task, defaultExpanded = false }: { task: AsyncTaskRecord; defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const qc = useQueryClient();
  const isDeveloper = useAuthStore((s) => !!s.user?.is_developer);
  const isActive = (s: TaskStatus) => s === "pending" || s === "running";

  const { data: live } = useQuery({
    queryKey: ["bg-task", task.id],
    queryFn: () => bgTasksApi.retrieve(task.id),
    refetchInterval: isActive(task.status) ? 2000 : false,
    initialData: task,
    enabled: isActive(task.status),
  });

  // Log WORKERA (DEV): ładowany dopiero po rozwinięciu wiersza i tylko dla
  // dewelopera — bywa duży, a backend i tak odpowie 403 komukolwiek innemu.
  // Odświeżany, dopóki zadanie biegnie: to jest widok „co się dzieje w procesie".
  const workerLog = useQuery({
    queryKey: ["bg-task-worker-log", task.id],
    queryFn: () => bgTasksApi.workerLog(task.id),
    enabled: expanded && isDeveloper,
    refetchInterval: expanded && isDeveloper && isActive(task.status) ? 3000 : false,
  });

  const cancel = useMutation({
    mutationFn: () => bgTasksApi.cancel(task.id),
    onSuccess: (updated) => {
      qc.setQueryData(["bg-task", task.id], updated);
      qc.invalidateQueries({ queryKey: ["bg-tasks"] });
    },
  });

  const display = live ?? task;

  // Exact build failures (np. traceback z kontenera qgis_builder) land in
  // result.dev_errors — rendered in their own DEV-only section below, and kept
  // out of the generic "Wynik" JSON dump so it stays readable.
  const devErrors = (Array.isArray(display.result?.dev_errors)
    ? display.result.dev_errors
    : []) as TaskDevError[];
  const shownResult =
    display.result && devErrors.length > 0
      ? Object.fromEntries(Object.entries(display.result).filter(([k]) => k !== "dev_errors"))
      : display.result;

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString("pl-PL", { hour12: false }) : "—";

  return (
    <div className="rounded-lg overflow-hidden">
      <div className="flex items-center bg-white dark:bg-gray-800">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750 text-left"
        >
          {expanded
            ? <ChevronDown size={16} className="shrink-0 text-gray-400" />
            : <ChevronRight size={16} className="shrink-0 text-gray-400" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {/* min-w-0 is what lets `truncate` win over the long task names
                  (e.g. "Formatki: …" with the whole order title inside) — a flex
                  item defaults to min-width:auto and would otherwise push the
                  status badge and the download button out of the card. */}
              <span
                className="min-w-0 font-medium text-sm text-gray-900 dark:text-white truncate"
                title={display.display_name || display.task_name}
              >
                {display.display_name || display.task_name}
              </span>
              <StatusBadge status={display.status} />
              {display.is_system && (
                <span className="inline-flex shrink-0 items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                  systemowe
                </span>
              )}
            </div>
            <div className="text-xs text-gray-400 mt-0.5 flex gap-3 flex-wrap">
              <span>Utworzono: {fmt(display.created_at)}</span>
              {display.triggered_by_name && <span>Przez: {display.triggered_by_name}</span>}
              {display.duration_seconds != null && <span>Czas: {display.duration_seconds}s</span>}
            </div>
          </div>
        </button>
        {display.result_file?.url && (
          <a
            href={display.result_file.url}
            download={display.result_file.filename}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 px-3 py-1.5 mr-3 text-xs text-[var(--color-primary)] border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shrink-0"
            title={`Pobierz ${display.result_file.filename}`}
          >
            <Download size={12} />
            Pobierz
          </a>
        )}
        {isActive(display.status) && (
          <button
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending}
            className="flex items-center gap-1 px-3 py-1.5 mr-3 text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 transition-colors shrink-0"
            title="Anuluj zadanie"
          >
            {cancel.isPending ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
            Anuluj
          </button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 space-y-3">
          <div className="text-xs text-gray-500 font-mono">{display.celery_task_id}</div>
          <div className="text-xs text-gray-400 grid grid-cols-2 gap-x-4 gap-y-1">
            <span>Zadanie: <span className="font-mono text-gray-600 dark:text-gray-300">{display.task_name}</span></span>
            {display.started_at && <span>Start: {fmt(display.started_at)}</span>}
            {display.finished_at && <span>Koniec: {fmt(display.finished_at)}</span>}
          </div>

          {display.log && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Log</p>
              <pre className="text-xs bg-black text-green-400 rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap">
                {display.log}
              </pre>
            </div>
          )}

          {display.result_file?.url && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Plik</p>
              <a
                href={display.result_file.url}
                download={display.result_file.filename}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-[var(--color-primary)] rounded hover:opacity-90 transition-opacity"
              >
                <Download size={14} />
                Pobierz {display.result_file.filename}
                {display.result_file.size_bytes != null && (
                  <span className="text-white/70 text-xs">({fmtBytes(display.result_file.size_bytes)})</span>
                )}
              </a>
            </div>
          )}

          {shownResult && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Wynik</p>
              <pre className="text-xs bg-gray-100 dark:bg-gray-800 rounded p-3 overflow-auto max-h-48">
                {JSON.stringify(shownResult, null, 2)}
              </pre>
            </div>
          )}

          {display.error && (
            <div>
              <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-1">Błąd</p>
              <pre className="text-xs bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 rounded p-3 overflow-auto max-h-48 whitespace-pre-wrap">
                {display.error}
              </pre>
            </div>
          )}

          {isDeveloper && (
            <div>
              <p className="text-xs font-semibold text-amber-600 dark:text-amber-500 uppercase tracking-wide mb-1">
                Log workera Celery (DEV)
                {workerLog.data && (
                  <span className="ml-2 normal-case font-normal text-gray-400">
                    {fmtBytes(workerLog.data.size)}
                  </span>
                )}
              </p>
              {workerLog.isPending && (
                <p className="text-xs text-gray-400">Ładowanie…</p>
              )}
              {workerLog.isError && (
                <p className="text-xs text-red-500">Nie udało się pobrać logu workera.</p>
              )}
              {workerLog.data && workerLog.data.size === 0 && (
                <p className="text-xs text-gray-400">
                  Brak — zadanie uruchomiono przed włączeniem zapisu logu workera albo proces nic nie zalogował.
                </p>
              )}
              {workerLog.data && workerLog.data.size > 0 && (
                <pre className="text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-auto max-h-96 whitespace-pre-wrap">
                  {workerLog.data.worker_log}
                </pre>
              )}
            </div>
          )}

          {isDeveloper && devErrors.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-600 dark:text-amber-500 uppercase tracking-wide mb-1">
                Szczegóły techniczne (DEV)
              </p>
              <div className="space-y-2">
                {devErrors.map((e, i) => (
                  <div key={i}>
                    {(e.version || e.source) && (
                      <p className="text-xs text-gray-500 mb-0.5">
                        {[e.version, e.source && `źródło: ${e.source}`].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <pre className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap">
                      {[e.error, e.traceback].filter(Boolean).join("\n\n")}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
